import { useEffect, useReducer } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';

import type { AIPlugin } from '../../../aiPluginType';

export type State = 'discard' | 'loading' | 'idle' | 'error' | 'prompt' | 'acceptReject';

export type Action =
	| { type: 'discard' }
	| { type: 'cancel' }
	| { type: 'reset' }
	| { type: 'terminate' }
	| { type: 'show-accept-reject' };

interface UIState {
	discard: boolean;
	showAcceptReject: boolean;
	terminated: boolean;
}

const initialState: UIState = {
	discard: false,
	terminated: false,
	showAcceptReject: false,
};

function reducer(state: UIState, action: Action): UIState {
	switch (action.type) {
		case 'discard':
			return { ...state, discard: true };
		case 'cancel':
			return { ...state, discard: false };
		case 'terminate':
			return { ...state, terminated: true };
		case 'reset':
			return { discard: false, terminated: false, showAcceptReject: false };
		case 'show-accept-reject':
			return { ...state, showAcceptReject: true };
		default:
			return state;
	}
}

/**
 * LEGACY MODE (xstate) - when feature flag is OFF
 */
const useUIStateLegacy = (api?: ExtractInjectionAPI<AIPlugin>): [State, React.Dispatch<Action>] => {
	const [localUI, dispatch] = useReducer(reducer, initialState);

	const { nextSessionId } = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => ({
		nextSessionId: state.aiExperienceState?.aiDecorationExperiencePluginState?.nextSessionId,
	}));

	useEffect(() => {
		if (!nextSessionId) {
			dispatch({ type: 'reset' });
		}
	}, [nextSessionId]);

	if (nextSessionId && !localUI.terminated) {
		if (localUI.discard) {
			return ['discard', dispatch];
		}
		return ['loading', dispatch];
	}
	return ['idle', () => {}];
};

/**
 * MIGRATED MODE (ProseMirror native) - when feature flag is ON
 * Uses ai-session-context plugin status as source of truth.
 */
const useUIStateMigrated = (
	api?: ExtractInjectionAPI<AIPlugin>,
): [State, React.Dispatch<Action>] => {
	const [localUI, dispatch] = useReducer(reducer, initialState);

	const { nextSessionId, status } = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) => ({
			nextSessionId: state.aiExperienceState?.aiDecorationExperiencePluginState?.nextSessionId,
			status: state.aiExperienceState?.aiSessionPluginState?.status,
		}),
	);

	// After terminating (e.g. discard confirmed), keep UI suppressed until the session fully ends.
	// Otherwise we can briefly show "loading" while `nextSessionId` is still present.
	useEffect(() => {
		if (localUI.terminated && !nextSessionId) {
			dispatch({ type: 'reset' });
		}
	}, [localUI.terminated, nextSessionId]);

	if (localUI.terminated) {
		return ['idle', dispatch];
	}

	if (localUI.discard) {
		return ['discard', dispatch];
	}

	// Active session that's not in error/idle = show loading
	if (nextSessionId && status !== 'error' && status !== 'idle') {
		return ['loading', dispatch];
	}

	if (localUI.showAcceptReject && !nextSessionId) {
		return ['acceptReject', dispatch];
	}

	// Map plugin status to UI state
	switch (status) {
		case 'prompt':
			return ['prompt', dispatch];
		case 'streaming':
		case 'complete':
			return ['loading', dispatch];
		case 'error':
			return ['error', dispatch];
		case 'idle':
		default:
			return ['idle', dispatch];
	}
};

export const useUIState = conditionalHooksFactory(
	() => fg('platform_editor_ai_migrate_from_xstate'),
	useUIStateMigrated,
	useUIStateLegacy,
);
